import { useState } from "react";
import { updateProfile, changePassword, regenerateObsToken, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Copy } from "lucide-react";

const Panel = ({ title, children, testid }) => (
    <div className="border border-white/10 bg-surface p-6" data-testid={testid}>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-5">{title}</div>
        {children}
    </div>
);

const inputCls =
    "w-full bg-obsidian border border-white/10 text-white px-3 py-3 font-mono text-sm outline-none focus:border-electric transition-colors";

function useSettingsActions(user, setUser) {
    const [busy, setBusy] = useState(null);
    const [confirmRegen, setConfirmRegen] = useState(false);

    const run = async (key, action) => {
        setBusy(key);
        try {
            await action();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(null);
        }
    };

    const saveName = (displayName) =>
        run("name", async () => {
            const updated = await updateProfile({ display_name: displayName });
            setUser((prev) => ({ ...prev, display_name: updated.display_name }));
            toast.success("Display name updated");
        });

    const savePassword = (currentPw, newPw, onDone) =>
        run("password", async () => {
            await changePassword({
                current_password: user?.has_password ? currentPw : undefined,
                new_password: newPw,
            });
            setUser((prev) => ({ ...prev, has_password: true }));
            onDone();
            toast.success("Password updated");
        });

    const regenerate = () => {
        if (!confirmRegen) {
            setConfirmRegen(true);
            setTimeout(() => setConfirmRegen(false), 4000);
            return;
        }
        setConfirmRegen(false);
        run("regen", async () => {
            const res = await regenerateObsToken();
            setUser((prev) => ({ ...prev, obs_token: res.obs_token }));
            toast.success("OBS token regenerated — update your OBS Browser Source with the new link");
        });
    };

    return { busy, confirmRegen, saveName, savePassword, regenerate };
}

const ProfilePanel = ({ user, busy, onSave }) => {
    const [displayName, setDisplayName] = useState(user?.display_name || "");
    return (
        <Panel title="Profile" testid="settings-profile-panel">
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSave(displayName);
                }}
                className="flex flex-col sm:flex-row gap-3"
            >
                <input
                    required maxLength={60} value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className={inputCls + " flex-1"}
                    data-testid="settings-name-input"
                />
                <button
                    type="submit" disabled={busy === "name"}
                    className="bg-blaze hover:bg-blazeHover disabled:opacity-50 text-white font-mono uppercase text-[11px] tracking-[0.2em] px-6 py-3 flex items-center justify-center gap-2 transition-colors"
                    data-testid="settings-name-save"
                >
                    {busy === "name" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Save
                </button>
            </form>
            <div className="font-mono text-[10px] text-white/30 mt-3">Signed in as {user?.email}</div>
        </Panel>
    );
};

const PasswordPanel = ({ user, busy, onSave }) => {
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");

    const submit = (e) => {
        e.preventDefault();
        if (newPw !== confirmPw) {
            toast.error("New passwords do not match");
            return;
        }
        onSave(currentPw, newPw, () => {
            setCurrentPw("");
            setNewPw("");
            setConfirmPw("");
        });
    };

    return (
        <Panel title={user?.has_password ? "Change Password" : "Set a Password"} testid="settings-password-panel">
            <form onSubmit={submit} className="space-y-3">
                {user?.has_password && (
                    <input
                        type="password" required placeholder="Current password" value={currentPw}
                        onChange={(e) => setCurrentPw(e.target.value)}
                        className={inputCls} data-testid="settings-current-password"
                    />
                )}
                <input
                    type="password" required minLength={6} placeholder="New password (min 6 chars)" value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    className={inputCls} data-testid="settings-new-password"
                />
                <input
                    type="password" required placeholder="Confirm new password" value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    className={inputCls} data-testid="settings-confirm-password"
                />
                <button
                    type="submit" disabled={busy === "password"}
                    className="bg-transparent border border-white/20 hover:border-blaze hover:text-blaze disabled:opacity-50 text-white font-mono uppercase text-[11px] tracking-[0.2em] px-6 py-3 flex items-center gap-2 transition-colors"
                    data-testid="settings-password-save"
                >
                    {busy === "password" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Update Password
                </button>
            </form>
        </Panel>
    );
};

const ObsTokenPanel = ({ obsUrl, busy, confirmRegen, onRegenerate }) => {
    const copyLink = () => {
        navigator.clipboard
            .writeText(obsUrl)
            .then(() => toast.success("OBS link copied"))
            .catch(() => toast.error("Copy failed — select the link and copy manually"));
    };

    return (
        <Panel title="OBS Stream Token" testid="settings-obs-panel">
            <div className="flex gap-2 mb-3">
                <input
                    readOnly value={obsUrl} onFocus={(e) => e.target.select()}
                    className={inputCls + " flex-1 min-w-0 text-white/70 text-xs"}
                    data-testid="settings-obs-link"
                />
                <button
                    onClick={copyLink}
                    className="px-4 border border-white/20 hover:border-white/60 text-white transition-colors"
                    data-testid="settings-obs-copy"
                >
                    <Copy className="w-3.5 h-3.5" />
                </button>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 leading-relaxed mb-4">
                This private link powers your OBS Browser Source. Regenerating it immediately
                invalidates the old link — update OBS after.
            </p>
            <button
                onClick={onRegenerate} disabled={busy === "regen"}
                className={`font-mono uppercase text-[11px] tracking-[0.2em] px-6 py-3 flex items-center gap-2 transition-colors ${
                    confirmRegen
                        ? "bg-blaze text-white"
                        : "bg-transparent border border-blaze/50 text-blaze hover:bg-blaze hover:text-white"
                } disabled:opacity-50`}
                data-testid="settings-obs-regenerate"
            >
                {busy === "regen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                {confirmRegen ? "Click again to confirm" : "Regenerate OBS Token"}
            </button>
        </Panel>
    );
};

export default function Settings() {
    const { user, setUser } = useAuth();
    const actions = useSettingsActions(user, setUser);
    const obsUrl = user?.obs_token ? `${window.location.origin}/obs?token=${user.obs_token}` : "";

    return (
        <div className="max-w-3xl space-y-6" data-testid="settings-page">
            <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze">/ Settings</div>
                <h1 className="font-display font-bold uppercase text-3xl mt-2 tracking-tight">Account Settings</h1>
            </div>
            <ProfilePanel user={user} busy={actions.busy} onSave={actions.saveName} />
            <PasswordPanel user={user} busy={actions.busy} onSave={actions.savePassword} />
            <ObsTokenPanel
                obsUrl={obsUrl}
                busy={actions.busy}
                confirmRegen={actions.confirmRegen}
                onRegenerate={actions.regenerate}
            />
        </div>
    );
}
